import { SyncWarningBadge } from '@/components/shared/SyncStatusBadge';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Languages } from 'lucide-react';

export interface AttractionThemeTranslation {
  id: string;
  themeId: string;
  languageId: string;
  name: string;
  description: string | null;
  longDescription: string | null;
  voiceoverId: string | null;
  longVoiceoverId: string | null;
  createdAt: string;
  updatedAt: string;
  theme?: {
    id: string;
    name: string | null;
    attraction?: {
      id: string;
      name: string;
    };
    themeType?: {
      name: string;
    };
  };
  language?: {
    id: string;
    name: string;
    code: string;
  };
  voiceover?: {
    id: string;
    name: string;
    fileName: string;
    fileType: string;
    duration?: number;
    url?: string;
  } | null;
  longVoiceover?: {
    id: string;
    name: string;
    fileName: string;
    fileType: string;
    duration?: number;
    url?: string;
  } | null;
  // Sync status fields (computed by API)
  isNameOutdated?: boolean | null;
  isDescriptionOutdated?: boolean | null;
  isLongDescriptionOutdated?: boolean | null;
}

/**
 * Factory function to create attraction theme translations config for a specific theme
 */
export const createAttractionThemeTranslationsConfig = (
  attractionId: string,
  themeId: string,
  options?: {
    onGenerateTranslations?: () => void;
  },
): CrudConfig<AttractionThemeTranslation> => ({
  entityName: 'Theme Translation',
  entityNamePlural: 'Theme Translations',

  endpoints: {
    list: `/attractions/${attractionId}/themes/${themeId}/translations`,
    single: (id: string) => `/attractions/${attractionId}/themes/${themeId}/translations/${id}`,
    create: `/attractions/${attractionId}/themes/${themeId}/translations`,
    update: (id: string) => `/attractions/${attractionId}/themes/${themeId}/translations/${id}`,
    delete: (id: string) => `/attractions/${attractionId}/themes/${themeId}/translations/${id}`,
  },

  fields: [
    {
      key: 'language',
      label: 'Language',
      type: 'custom',
      required: false,
      format: (
        _value: AttractionThemeTranslation['language'],
        fullRecord?: AttractionThemeTranslation,
      ) => {
        if (!fullRecord?.language) return '—';
        return (
          <div>
            <div className="font-medium">{fullRecord.language.name}</div>
            <div className="text-xs text-muted-foreground">{fullRecord.language.code}</div>
          </div>
        );
      },
    },
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      format: (value: string, fullRecord?: AttractionThemeTranslation) => {
        return (
          <div className="flex items-center gap-2">
            <span>{value}</span>
            <SyncWarningBadge
              isInSync={
                fullRecord?.isNameOutdated === true
                  ? false
                  : fullRecord?.isNameOutdated === false
                    ? true
                    : null
              }
              message="Source theme name has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'voiceoverId',
      label: 'Short Description Voiceover',
      type: 'audio',
      required: false,
      readonly: true,
    },
    {
      key: 'voiceover',
      label: 'Short Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: AttractionThemeTranslation['voiceover']) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={null}
            outOfSyncMessage="Description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'description',
      label: 'Short Description',
      type: 'richtext',
      required: false,
      format: (value: string | null, fullRecord?: AttractionThemeTranslation) => {
        return (
          <div className="flex flex-col gap-2">
            <div dangerouslySetInnerHTML={{ __html: value || '-' }} />
            <SyncWarningBadge
              isInSync={
                fullRecord?.isDescriptionOutdated === true
                  ? false
                  : fullRecord?.isDescriptionOutdated === false
                    ? true
                    : null
              }
              message="Source theme description has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'longVoiceoverId',
      label: 'Long Description Voiceover',
      type: 'audio',
      required: false,
      readonly: true,
    },
    {
      key: 'longVoiceover',
      label: 'Long Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: AttractionThemeTranslation['longVoiceover']) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={null}
            outOfSyncMessage="Long description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'longDescription',
      label: 'Long Description',
      type: 'richtext',
      required: false,
      format: (value: string | null, fullRecord?: AttractionThemeTranslation) => {
        return (
          <div className="flex flex-col gap-2">
            <div dangerouslySetInnerHTML={{ __html: value || '-' }} />
            <SyncWarningBadge
              isInSync={
                fullRecord?.isLongDescriptionOutdated === true
                  ? false
                  : fullRecord?.isLongDescriptionOutdated === false
                    ? true
                    : null
              }
              message="Source theme long description has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['language', 'name', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'description'],
    sortableFields: ['createdAt'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    emptyState: {
      icon: <Languages className="h-12 w-12" />,
      title: 'No translations yet',
      description: 'Generate translations to make this theme available in multiple languages',
      action: options?.onGenerateTranslations
        ? {
            label: 'Generate Translations',
            icon: Languages,
            onClick: options.onGenerateTranslations,
          }
        : undefined,
    },
    actions: {
      create: {
        enabled: false, // Translations are generated, not manually created
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: false, // Translations are generated, not manually deleted
      },
    },
  },

  form: {
    sections: [
      {
        title: 'Translation Content',
        description: 'Edit theme translation text',
        fields: ['name', 'description', 'longDescription'],
        columns: 1,
      },
    ],
  },

  detailView: {
    sections: [
      {
        title: 'Translation Information',
        description: 'AI-generated theme translation content',
        fields: ['language', 'name'],
        defaultExpanded: true,
      },
      {
        title: 'Descriptions',
        description: 'Short and long translated descriptions with voiceovers',
        fields: ['voiceover', 'description', 'longVoiceover', 'longDescription'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },
});
